import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Link, useParams } from "react-router-dom";
import { AccountPageWrapper } from "../AccountPageWrapper";
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  ButtonGroup,
  Card,
  HStack,
  Icon,
  Stack,
  StackSeparator,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { appRoutes } from "../../../../App";
import { AccountView } from "../../models/AccountView";
import { useStores } from "../../../shared/stores/RootStore";
import { AddIcon, ExternalLinkIcon } from "../../../shared/views/icons";
import { toast } from "../../../shared/views/CustomToaster";

const i18Prefix = "account:Surveys";

const AccountSurveysPageBase: React.FC = () => {
  const accountId = useParams().accountId!;
  const { accountStore, authStore } = useStores();

  useEffect(() => {
    accountStore.fetchAccountSurveys(accountId);
    // eslint-disable-next-line
  }, [accountId]);

  const addSurvey = async (surveyId: string) => {
    await accountStore
      .addAccountAccess(surveyId)
      .then(() => {
        toast("success", t(`${i18Prefix}.addToast.accessGiven`));
      })
      .catch(() => {
        toast("error", t(`${i18Prefix}.addToast.accessFailed`));
      });
  };

  return (
    <AccountPageWrapper
      selectedView={AccountView.Surveys}
      accountId={accountId}
    >
      <Text mx={6} fontSize="2xl">
        {t(`${i18Prefix}.tab`)}
      </Text>

      {authStore.isSuperAdmin && (
        <Card.Root mx={6} overflow={"hidden"} variant={"outline"}>
          <Alert.Root status="info">
            <Alert.Indicator />
            <AlertDescription>{t(`${i18Prefix}.superAdmin`)}</AlertDescription>
          </Alert.Root>
        </Card.Root>
      )}

      <Box mx={6}>
        <Stack gap={0} separator={<StackSeparator />}>
          {accountStore.surveys.map((survey, i) => (
            <HStack key={i}>
              <Text flexGrow={1}>
                {`${survey.city}: `}
                <strong>{survey.name}</strong>
              </Text>

              {survey.permissions.canRead && (
                <Link to={appRoutes.survey(survey.id)} target="_blank">
                  <Icon color={"primary.600"} as={ExternalLinkIcon} />
                </Link>
              )}

              <ButtonGroup
                variant="ghost"
                attached
                size="sm"
                fontSize={"sm"}
                colorPalette={"primary"}
              >
                <Button
                  onClick={() => addSurvey(survey.id!)}
                  disabled={
                    survey.permissions?.canRead && !authStore.isSuperAdmin
                  }
                >
                  <AddIcon /> {t(`${i18Prefix}.add`)}
                </Button>
              </ButtonGroup>
            </HStack>
          ))}
        </Stack>
      </Box>
    </AccountPageWrapper>
  );
};

export const AccountSurveysPage = observer(AccountSurveysPageBase);
